/**
 * ReasonModal — the confirm dialog for a governed reversal.
 *
 * Every reversal RPC (migration 736/738, `src/lib/reversals/reversalsService.ts`)
 * requires a non-empty written reason and can be refused once something
 * downstream has consumed the act. This modal is the one place that requires
 * the reason and surfaces that refusal — built on the shared `Modal` in
 * `@/components/ds` rather than a hand-rolled dialog (152 of those already
 * exist; see Modal.tsx's own header for why this one is reused instead).
 *
 * Not reversal-specific by name so any "type a reason, confirm, see a
 * server refusal inline" flow can reuse it. Callers: `ReverseActionButton`
 * (reversals) and `ConditionsPanel` (moving a condition), which until
 * 2026-10-07 asked through the browser's own `window.prompt`.
 */
import { useEffect, useState } from 'react';
import { Modal, TextArea } from '@/components/ds';
import { Callout } from '@/components/ds';
import { Button } from '@/components/ui/Button';

export interface ReasonModalProps {
  open: boolean;
  onClose: () => void;
  /** Dialog title, e.g. "Unbind from cycle". */
  title: string;
  /** Plain statement of what will be undone. */
  explain: React.ReactNode;
  /** Label for the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** True while the reversal RPC is in flight — disables inputs. */
  busy?: boolean;
  /** Refusal text to show inline, keeping the modal open. */
  errorMessage?: string | null;
  /** The confirm button's look: "danger" for undoing something (the default), "primary" for an ordinary act. */
  confirmVariant?: 'danger' | 'primary';
  /** The field's label. Defaults to "Reason (required)". */
  reasonLabel?: string;
  /** The field's placeholder. Defaults to "Why are you undoing this?". */
  reasonPlaceholder?: string;
  onConfirm: (reason: string) => void;
}

export function ReasonModal({
  open,
  onClose,
  title,
  explain,
  confirmLabel = 'Confirm',
  busy = false,
  errorMessage,
  confirmVariant = 'danger',
  reasonLabel = 'Reason (required)',
  reasonPlaceholder = 'Why are you undoing this?',
  onConfirm,
}: ReasonModalProps) {
  const [reason, setReason] = useState('');

  // Start fresh each time the dialog opens, so a prior refusal's reason
  // doesn't linger silently after the user tries a different act.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const trimmed = reason.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      dismissible={!busy}
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            size="sm"
            onClick={() => onConfirm(trimmed)}
            disabled={busy || !trimmed}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-[var(--text-sec)]">{explain}</p>
        <div>
          <label
            htmlFor="reversal-reason"
            className="mb-1 block text-xs font-medium text-[var(--text-ter)]"
          >
            {reasonLabel}
          </label>
          <TextArea
            id="reversal-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={reasonPlaceholder}
            rows={3}
            disabled={busy}
            autoFocus
          />
        </div>
        {errorMessage && <Callout tone="coral">{errorMessage}</Callout>}
      </div>
    </Modal>
  );
}
